import { AddCircle } from 'assets/icons/AddCircle'
import { CloseIcon } from 'assets/icons/CloseIcon'
import RemoveCircle from 'assets/icons/RemoveCircle'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { Input } from 'components/Input/Input'
import { Modal } from 'components/Modal/Modal'
import { ISelectValue } from 'constants/globalTypes'
import { addField, editField } from 'features/fields/fieldsSlice'
import { addInvField, editInvField } from 'features/inventory/invSettingsFieldsSlice'
import { getUnits } from 'features/units/unitsSlice'
import {
  addWorkerComponent,
  editWorkerComponent,
} from 'features/workerComponents/workerComponentsSlice'
import useModalClose from 'hooks/useModalClose'
import { workerComponentTypes } from 'pages/WorkerComponents/workerComponentsData'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { Button, CursorPointer, Flex, ThinSeparator } from 'styling/GlobalStyles'
import { OtherText, TitleText } from 'styling/Typography'
import useFocusFirstInput from 'utils/useFocusFirstInput'

export const FieldsModal = ({
  closeModal,
  value,
  workerComponents,
}: {
  closeModal: any
  value?: IFieldsModal
  workerComponents?: boolean
}) => {
  const [state, setState] = useState<IFieldsModal>(
    value || {
      name: '',
      type: null,
      dropDownItems: [],
      unit: null,
    },
  )
  const { units } = useSelector((state: RootState) => state.units)
  const dispatch = useDispatch<AppDispatch>()
  useFocusFirstInput()
  useModalClose(closeModal)

  useEffect(() => {
    dispatch(getUnits())
  }, [])

  const handleSave = async () => {
    let resp
    if (workerComponents) {
      resp = await dispatch(value?.id ? editWorkerComponent(state) : addWorkerComponent(state))
    } else {
      resp = await dispatch(value?.id ? editField(state) : addField(state))
    }

    if (resp.meta.requestStatus === 'fulfilled') {
      closeModal()
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, id?: string) => {
    setState((prev) => {
      if (id) {
        return {
          ...prev,
          dropDownItems: prev.dropDownItems.map((item) =>
            item.id === Number(id) ? { ...item, name: e.target.value } : item,
          ),
        }
      } else {
        return { ...prev, name: e.target.value }
      }
    })
  }

  const handleSelect = (value: ISelectValue, name: string, id?: string) => {
    setState((prev) => {
      if (name == 'workerComponentType') {
        return { ...prev, workerComponentType: value }
      } else if (id) {
        return {
          ...prev,
          dropDownItems: prev.dropDownItems.map((item) =>
            item.id === Number(id) ? { ...item, type: value } : item,
          ),
        }
      } else {
        return {
          ...prev,
          type: value,
          dropDownItems:
            value.value == 3
              ? [
                { id: new Date().getMilliseconds(), name: '', type: null },
                { id: new Date().getMilliseconds() + 1, name: '', type: null },
              ]
              : [],
        }
      }
    })
  }

  const handleAddSubfield = () => {
    setState((prev) => {
      return {
        ...prev,
        dropDownItems: [
          ...prev.dropDownItems,
          { id: new Date().getMilliseconds(), name: '', type: null },
        ],
      }
    })
  }

  const handleRemoveField = (id: number) => {
    setState((prev) => {
      return { ...prev, dropDownItems: prev.dropDownItems.filter((item) => item.id !== id) }
    })
  }

  const handleUnit = (v: ISelectValue) => {
    setState((prev) => ({ ...prev, unit: v }))
  }

  const isDisabled = useMemo(() => {
    return (
      !state.name ||
      !state.type?.value ||
      (state.type.value == 3 && state.dropDownItems.some((d) => !d.name || !d.type?.value)) ||
      (state.type?.value == 2 && !state.unit?.value) ||
      (workerComponents && !state.workerComponentType?.value)
    )
  }, [state])

  const parsedUnits = useMemo(
    () => (units ? units.map((u) => ({ label: u.name, value: u.id })) : []),
    [units],
  )

  return (
    <Modal black>
      <Flex column between center>
        <Flex column gap='1rem' width='100%'>
          <Flex between width='100%' center style={{ marginBottom: '1rem' }}>
            <TitleText weight='bold'>{value?.id ? 'Edit' : 'Add'} Field</TitleText>
            <CursorPointer onClick={closeModal}>
              <CloseIcon />
            </CursorPointer>
          </Flex>
          <Flex width='100%' column>
            <Flex width='100%' gap='1rem' mb='16' padding='1px'>
              <Input
                name='name'
                placeholder='Fuel Tank Capacity...'
                onChange={handleChange}
                labelText='Name'
                nomarg
                value={state.name}
              />
              <HonestSelectInput
                nomarg
                onChange={handleSelect}
                options={fieldTypes}
                labelText='Type'
                defaultValue={state?.type?.value}
              />
            </Flex>

            <Flex width='100%' column gap='8px' mb='16' maxHeight='320px' overflow>
              {state.type?.value == 3 && (
                <>
                  <OtherText weight='bold'>Subfields:</OtherText>
                  <ThinSeparator margin='2px' />
                </>
              )}
              {state.dropDownItems.map((s) => (
                <Flex center key={s.id} gap='1rem'>
                  <Input
                    name='name'
                    onChange={(e) => handleChange(e, String(s.id))}
                    placeholder='Fuel Type...'
                    labelText='Name'
                    nomarg
                    value={s.name}
                  />
                  <HonestSelectInput
                    nomarg
                    onChange={(v: ISelectValue, n) => handleSelect(v, n, String(s.id))}
                    options={fieldTypes.slice(0, -1)}
                    labelText='Type'
                    defaultValue={s.type?.value}
                  />
                  {state.dropDownItems.length > 2 && (
                    <CursorPointer
                      style={{ marginTop: 20 }}
                      onClick={() => handleRemoveField(s.id)}
                    >
                      <RemoveCircle />
                    </CursorPointer>
                  )}
                </Flex>
              ))}
            </Flex>
            {state.type?.value == 3 && (
              <Flex width='100%' mb='16'>
                <CursorPointer onClick={handleAddSubfield}>
                  <AddCircle />
                </CursorPointer>
              </Flex>
            )}
            <Flex width={workerComponents && state.type?.value == 2 ? '100%' : '50&'} gap='1rem'>
              {state.type?.value == 2 && (
                <HonestSelectInput
                  onChange={handleUnit}
                  options={parsedUnits}
                  labelText='Measurement unit'
                  defaultValue={state?.unit?.value}
                  width={'100%'}
                  nomarg
                />
              )}
              {workerComponents && (
                <HonestSelectInput
                  onChange={handleSelect}
                  name='workerComponentType'
                  options={workerComponentTypes}
                  labelText='Component Type'
                  defaultValue={state.workerComponentType?.value}
                  width={'100%'}
                  nomarg
                />
              )}
            </Flex>
          </Flex>
        </Flex>
        <Button style={{ marginTop: 16 }} disabled={isDisabled} orange onClick={handleSave}>
          Save
        </Button>
      </Flex>
    </Modal>
  )
}

export interface IFieldsModal {
  id?: number
  name: string
  type: ISelectValue
  dropDownItems: {
    id: number
    name: string
    type: ISelectValue
  }[]
  unit: ISelectValue
  workerComponentType?: ISelectValue
}

export const fieldTypes = [
  { label: 'Text', value: 1 },
  { label: 'Number', value: 2 },
  { label: 'Dropdown', value: 3 },
]
